// State for the shared filter panel.
//
// Both boards stage their filters: you change controls, then press Search. That matters on the
// pipeline, where one Search fans out into four column queries — refetching on every keystroke
// would be sixteen requests to type a price. So this hook keeps TWO copies: `values` is what
// the controls show, `applied` is what the results on screen were actually fetched with. A
// page pages through results against `applied`, never against `values`, or paging picks up
// half-typed filters the user never submitted.

import { useCallback, useRef, useState } from "react";
import { emptyValues, fromStored, toParams, toStored } from "./filterSpec";

/**
 * @param {object} initial  starting values (merged over the blank set)
 * @returns {{
 *   values, applied, set, setMany, reset, apply, toParams, toStored, load
 * }}
 */
export default function useListingFilters(initial = {}) {
  const base = useRef({ ...emptyValues(), ...initial });
  const [values, setValues] = useState(base.current);
  const [applied, setApplied] = useState(base.current);

  const set = useCallback((key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
  }, []);

  const setMany = useCallback((patch) => {
    setValues((v) => ({ ...v, ...patch }));
  }, []);

  /** Snapshot the current controls as the applied set, and hand it back.
   *
   * Returns the object rather than making the caller read `applied` back, so it can fetch with
   * it immediately instead of waiting a render for setState to land. `values` is a dependency
   * on purpose: reading it out of a state updater's side effect would depend on React running
   * that updater eagerly, which it does not promise. */
  const apply = useCallback(() => {
    setApplied(values);
    return values;
  }, [values]);

  /** Back to the starting set (which may carry a page's defaults, e.g. the user's hoods). */
  const reset = useCallback((overrides = {}) => {
    const next = { ...emptyValues(), ...base.current, ...overrides };
    setValues(next);
    return next;
  }, []);

  /** Load a stored payload (a saved search) into the controls and apply it in one step. */
  const load = useCallback((stored) => {
    const next = fromStored(stored);
    setValues(next);
    setApplied(next);
    return next;
  }, []);

  return {
    values,
    applied,
    set,
    setMany,
    apply,
    reset,
    load,
    toParams: (v = values, params) => toParams(v, params),
    toStored: (v = values) => toStored(v),
  };
}
